import { merge as observableMerge, Observable, Subject } from 'rxjs';
import { delay, filter, map, pairwise, startWith, takeUntil, tap } from 'rxjs/operators';
import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatInput } from '@angular/material/input';
import { FormField, Forminator } from '@nuso/forms/data-access';
import { ForminatorErrorService } from '../forminator-error-service';
import { AbstractControl } from '@angular/forms';

@Directive()
export class BaseFormInput implements OnInit, OnDestroy {
  private forminatorErrorService = inject(ForminatorErrorService);

  @Input() field: FormField;
  @Output() actionButtonClicked = new EventEmitter<FormField>();
  @Output() fieldClicked = new EventEmitter<FormField>();
  @ViewChild(MatInput, {static: false}) matInput: MatInput;

  shutdown$ = new Subject<boolean>();

  showField$: Observable<boolean>;

  ngOnInit() {
    let controls: AbstractControl[] = [];

    if (this.field.showIfDependencies) {
      controls = [...controls, ...Forminator.dependencyControls(this.field, this.field.showIfDependencies)];
    }

    if (this.field.enableIfDependencies) {
      controls = [...controls, ...Forminator.dependencyControls(this.field, this.field.enableIfDependencies)];
    }

    const changes = controls.map(c => c.valueChanges);
    this.showField$ = observableMerge(...changes).pipe(
      startWith(true), // anything, it doesn't matter, just need to trigger a check
      tap(() => this.enableIf()),
      map(() => this.shouldShow())
    );

    this.showField$.pipe(
      takeUntil(this.shutdown$),
      pairwise(),
      filter(([shownBefore, showNow]) => !shownBefore && showNow || shownBefore && !showNow),
      delay(1), // need to do it on a different time slice
      tap(() => {
        this.field.control.updateValueAndValidity();
      })
    )
    .subscribe();
  }

  ngOnDestroy() {
    this.shutdown$.next(true);
  }

  focus() {
    if ( this.matInput ) {
      this.matInput.focus();
    }
  }

  hasDanger(): boolean {
    return !this.isDisabled() && (
      (this.field.control.touched && !this.field.control.valid)
      || (!!this.field.form?.submitted && !this.field.control.valid)
    );
  }

  isDisabled(): boolean {
    return this.field.control.disabled;
  }

  errorText() {
    return this.forminatorErrorService.errorMessage(this.field);
  }

  labelClass() {
    if (this.field.labelPosition === 'side') { return 'col-sm-2 col-form-label'; }
    if (this.field.labelPosition === 'hidden') { return 'sr-only'; }
    if (this.field.type === 'checkbox' ) { return 'hidden-xs-up'; }

    return 'control-label';
  }

  fieldGroupClass() {
    return this.field.labelPosition === 'side' ? 'row' : '';
  }

  inputGroupClass() {
    if (this.field.labelPosition === 'side') { return 'col-sm-10'; }
    return '';
  }

  protected shouldShow(): boolean {
    if (this.field.showIfDependencies) {
      return Forminator.dependenciesMet(this.field, this.field.showIfDependencies);
    }

    return true;
  }

  private enableIf() {
    if (!this.field.enableIfDependencies) {
      return;
    }

    if (Forminator.dependenciesMet(this.field, this.field.enableIfDependencies)) {
      this.field.control.enable();
      return;
    }

    this.field.control.disable();
    if (this.field.resetOnDisable) {
      this.field.control.reset(undefined, { emitEvent: false });
    }
  }
}
